import React, { useState, useEffect, useRef, useContext } from "react";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  getGroupsByProductionIdOnce,
  getLocationsByProductionIdOnce,
} from "../util/db";
import SearchedLocationListItem from "./SearchedLocationListItem";
import { SearchContext } from "../providers/SearchProvider";

// ---------- helpers (dayKey-first) ----------
const pad2 = (n) => String(n).padStart(2, "0");

const startOfLocalDay = (dateLike) => {
  const d = dateLike instanceof Date ? new Date(dateLike) : new Date(dateLike);
  d.setHours(0, 0, 0, 0);
  return d;
};

const toDayKey = (dateLike) => {
  const d = startOfLocalDay(dateLike);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const dayKeyFromMs = (ms) => toDayKey(new Date(ms));

function SearchedLocationList(props) {
  const theme = useTheme();
  const locationsRef = useRef([]);
  const groupsRef = useRef([]);

  const [combinedLocsGroups, setCombinedLocsGrups] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  const { setPreventBlur } = useContext(SearchContext);

  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isXs = useMediaQuery(theme.breakpoints.down("xs"));

  const flexRow = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid gray",
    padding: 8,
    fontWeight: "900",
  };

  const flexCol = { marginRight: 10 };

  // fetch locations, groups, combine
  useEffect(() => {
    let isMounted = true;

    const fetchAllLocations = async () => {
      const fetchedLocations = await getLocationsByProductionIdOnce(
        props.productionId
      );
      locationsRef.current = fetchedLocations ?? [];
    };

    const fetchAllGroups = async () => {
      const fetchedGroups = await getGroupsByProductionIdOnce(
        props.productionId
      );
      groupsRef.current = fetchedGroups ?? [];
    };

    const combineLocationAndGroupData = async () => {
      try {
        setLoading(true);

        await Promise.all([fetchAllLocations(), fetchAllGroups()]);

        const merged = (locationsRef.current ?? []).map((location) => {
          const matchedGroup = (groupsRef.current ?? []).find(
            (group) => group.id === location.groupId
          );

          if (!matchedGroup) return location;

          const groupStartDay =
            matchedGroup.startDay ??
            (matchedGroup.startDate != null
              ? dayKeyFromMs(matchedGroup.startDate)
              : null) ??
            (matchedGroup.day ? toDayKey(new Date(matchedGroup.day)) : null);

          const groupEndDay =
            matchedGroup.endDay ??
            (matchedGroup.endDate != null
              ? dayKeyFromMs(matchedGroup.endDate)
              : groupStartDay) ??
            groupStartDay;

          return {
            ...location,

            // group metadata for search UI
            groupName: matchedGroup.name,
            groupColor: matchedGroup.groupColor,

            // ✅ NEW: dayKey-first fields
            groupStartDay,
            groupEndDay,

            // ✅ optional convenience: single-day key
            groupDayKey: groupStartDay,

            // ⛑️ TEMP legacy passthrough (safe during migration)
            startDate: matchedGroup.startDate,
            endDate: matchedGroup.endDate,
            day: matchedGroup.day,
          };
        });

        // newest first by dayKey (lexicographic works for YYYY-MM-DD)
        const sorted = merged.sort((a, b) => {
          const aKey = a.groupStartDay ?? "0000-00-00";
          const bKey = b.groupStartDay ?? "0000-00-00";
          return bKey.localeCompare(aKey);
        });

        if (!isMounted) return;
        setCombinedLocsGrups(sorted);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching locations/groups:", error);
        if (!isMounted) return;
        setCombinedLocsGrups([]);
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    combineLocationAndGroupData();

    return () => {
      isMounted = false;
    };
  }, [props.productionId]);

  // react to search text changing
  useEffect(() => {
    const matchesSearch = (obj, searchStr) => {
      const needle = (searchStr ?? "").toLowerCase();
      if (!needle) return true;

      return Object.values(obj ?? {}).some((value) => {
        if (typeof value === "object" && value !== null) {
          return matchesSearch(value, needle);
        }
        return (
          typeof value === "string" && value.toLowerCase().includes(needle)
        );
      });
    };

    if (props.searchText) {
      const filtLoc = (combinedLocsGroups ?? []).filter((loc) =>
        matchesSearch(loc, props.searchText)
      );
      setFilteredLocations(filtLoc);
    } else {
      setFilteredLocations(combinedLocsGroups ?? []);
    }
  }, [combinedLocsGroups, props.searchText]);

  const handleMouseEnter = () => setPreventBlur(true);
  const handleMouseLeave = () => setPreventBlur(false);

  return (
    <>
      <Paper style={{ backgroundColor: theme.backgroundColor }}>
        {loading && (
          <Box py={2} px={3} align="center">
            {!combinedLocsGroups.length && <span>FETCHING LOCATIONS...</span>}
          </Box>
        )}

        {!loading && !filteredLocations.length && props.searchText && (
          <Box py={2} px={3} align="center">
            <span>Your search criteria did not match any locations.</span>
          </Box>
        )}

        {filteredLocations && filteredLocations.length > 0 && (
          <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            {!isXs && (
              <li style={flexRow}>
                <div style={{ ...flexCol, width: "200px" }}>TYPE</div>
                <div style={{ ...flexCol, width: "200px" }}>NAME</div>
                <div style={{ ...flexCol, width: "200px" }}>GROUP</div>
                <div style={{ ...flexCol, width: "400px" }}>ADDRESS</div>
                <div style={{ ...flexCol, width: "100px" }}>CATEGORY</div>
                <div style={{ ...flexCol, width: "150px" }}>DAY</div>
              </li>
            )}

            {filteredLocations.map((location, index) => (
              <SearchedLocationListItem
                key={location.id ?? index}
                location={location}
                isXs={isXs}
                locationLength={combinedLocsGroups.length}
                index={index}
                isSmall={isSmall}
              />
            ))}
          </div>
        )}
      </Paper>
    </>
  );
}

export default SearchedLocationList;